import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'

import {
  type FilterValue,
  type DateRange,
} from '../components/multi-select/filterTypeConfig'

export const useDateRange = (
  selectedFilters: FilterValue[],
  setSelectedFilters: Dispatch<SetStateAction<FilterValue[]>>
) => {
  const [isInvalidRange, setIsInvalidRange] = useState(false)

  const validateDateRange = useCallback((updatedDates: DateRange) => {
    const { startDate, endDate, startTime, endTime } = updatedDates
    if (startDate && endDate) {
      const startDateTime = new Date(`${startDate}T${startTime || '00:00:00'}`)
      const endDateTime = new Date(`${endDate}T${endTime || '00:00:00'}`)
      return endDateTime <= startDateTime
    }
    return false
  }, [])

  const handleDateChange = useCallback(
    (key: string, date: Date | null) => {
      setSelectedFilters((prev: FilterValue[]) => {
        const existingFilter = prev.find((f) => f.key === 'date-range')
        const updatedDates: DateRange = {
          ...((existingFilter?.value as DateRange) || {
            startDate: null,
            endDate: null,
            startTime: '',
            endTime: '',
          }),
          [key]: date ? date.toISOString().split('T')[0] : null,
        }
        setIsInvalidRange(validateDateRange(updatedDates))
        if (existingFilter) {
          return prev.map((f) =>
            f.key === 'date-range'
              ? { key: 'date-range', value: updatedDates }
              : f
          )
        } else {
          return [...prev, { key: 'date-range', value: updatedDates }]
        }
      })
    },
    [setSelectedFilters]
  )

  const handleTimeChange = useCallback(
    (key: string, value: string) => {
      setSelectedFilters((prev: FilterValue[]) => {
        const existingFilter = prev.find((f) => f.key === 'date-range')
        const updatedDates: DateRange = {
          ...((existingFilter?.value as DateRange) || {
            startDate: null,
            endDate: null,
            startTime: '',
            endTime: '',
          }),
          [key]: value,
        }
        setIsInvalidRange(validateDateRange(updatedDates))
        if (existingFilter) {
          return prev.map((f) =>
            f.key === 'date-range'
              ? { key: 'date-range', value: updatedDates }
              : f
          )
        } else {
          return [...prev, { key: 'date-range', value: updatedDates }]
        }
      })
    },
    [setSelectedFilters]
  )

  const getDateRange = useCallback((): DateRange => {
    const dateRange = selectedFilters.find((f) => f.key === 'date-range')
    return (
      (dateRange?.value as DateRange) || {
        startDate: null,
        endDate: null,
        startTime: '',
        endTime: '',
      }
    )
  }, [selectedFilters])

  useEffect(() => {
    const dateRangeFilter = selectedFilters.find((f) => f.key === 'date-range')
    if (!dateRangeFilter) {
      setIsInvalidRange(false)
    }
  }, [selectedFilters])

  return {
    handleDateChange,
    handleTimeChange,
    isInvalidRange,
    getDateRange,
  }
}
